import { useEffect, useState } from "react";
import { adminService } from "../../services/adminService";
import Icon from "../../components/Common/Icon";
import { useToast } from "../../context/ToastContext";

const EMPTY_FORM = {
  email: "",
  fullName: "",
  phone: "",
  password: "",
};

export default function SystemAdminsPage() {
  const { showToast } = useToast();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // Status Change State
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await adminService.list();
      setAdmins(res.data.data ?? []);
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? "Yöneticiler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleOpenModal = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!form.email || !form.fullName || !form.password) {
      setFormError("Lütfen gerekli tüm alanları doldurun.");
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);
      await adminService.create(form);
      showToast("Yeni sistem yöneticisi başarıyla oluşturuldu.", "success");
      handleCloseModal();
      fetchAdmins();
    } catch (err) {
      setFormError(err.response?.data?.error ?? err.message ?? "Yönetici eklenirken hata oluştu");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (admin) => {
    const newActiveState = !admin.active;
    const confirmMessage = newActiveState
      ? `${admin.fullName} (${admin.email}) yöneticisini aktifleştirmek istiyor musunuz?`
      : `${admin.fullName} (${admin.email}) yöneticisini pasife almak istiyor musunuz?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setActionLoadingId(admin.id);
      await adminService.setActive(admin.id, newActiveState);
      showToast(
        `Yönetici ${newActiveState ? "aktifleştirildi" : "pasifleştirildi"}.`,
        "success"
      );
      fetchAdmins();
    } catch (err) {
      showToast(
        err.response?.data?.error ?? err.message ?? "Durum değiştirilemedi",
        "error"
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  // Filtered List
  const filteredAdmins = admins.filter(
    (a) =>
      a.fullName?.toLowerCase().includes(search.toLowerCase()) ||
      a.email?.toLowerCase().includes(search.toLowerCase()) ||
      a.phone?.includes(search)
  );

  // Statistics
  const totalCount = admins.length;
  const activeCount = admins.filter((a) => a.active).length;
  const inactiveCount = admins.filter((a) => !a.active).length;
  const twoFactorCount = admins.filter((a) => a.twoFactorEnabled).length;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-surface-card p-6 rounded-2xl border border-surface-border shadow-sm">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary-100 text-primary rounded-xl">
              <Icon name="admin_panel_settings" className="text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-on-surface">
                Sistem Yöneticileri
              </h1>
              <p className="text-sm text-on-surface-variant">
                Platform düzeyindeki süper admin kullanıcılarını yönetin ve yenilerini ekleyin.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={handleOpenModal}
          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-600 text-white font-medium rounded-xl shadow-md transition-all duration-200 cursor-pointer"
        >
          <Icon name="add" className="text-xl" />
          <span>Yeni Yönetici Ekle</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-surface-card p-5 rounded-2xl border border-surface-border flex items-center gap-4">
          <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
            <Icon name="groups" className="text-2xl" />
          </div>
          <div>
            <p className="text-xs font-medium text-on-surface-variant uppercase tracking-wider">
              Toplam Yönetici
            </p>
            <p className="text-2xl font-bold text-on-surface">{totalCount}</p>
          </div>
        </div>

        <div className="bg-surface-card p-5 rounded-2xl border border-surface-border flex items-center gap-4">
          <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600">
            <Icon name="check_circle" className="text-2xl" />
          </div>
          <div>
            <p className="text-xs font-medium text-on-surface-variant uppercase tracking-wider">
              Aktif Yöneticiler
            </p>
            <p className="text-2xl font-bold text-emerald-600">{activeCount}</p>
          </div>
        </div>

        <div className="bg-surface-card p-5 rounded-2xl border border-surface-border flex items-center gap-4">
          <div className="p-3 rounded-xl bg-rose-50 text-rose-600">
            <Icon name="block" className="text-2xl" />
          </div>
          <div>
            <p className="text-xs font-medium text-on-surface-variant uppercase tracking-wider">
              Pasif Yöneticiler
            </p>
            <p className="text-2xl font-bold text-rose-600">{inactiveCount}</p>
          </div>
        </div>

        <div className="bg-surface-card p-5 rounded-2xl border border-surface-border flex items-center gap-4">
          <div className="p-3 rounded-xl bg-purple-50 text-purple-600">
            <Icon name="security" className="text-2xl" />
          </div>
          <div>
            <p className="text-xs font-medium text-on-surface-variant uppercase tracking-wider">
              2FA Güvenlik
            </p>
            <p className="text-2xl font-bold text-purple-600">{twoFactorCount}</p>
          </div>
        </div>
      </div>

      {/* Main Table Container */}
      <div className="bg-surface-card rounded-2xl border border-surface-border shadow-sm overflow-hidden">
        {/* Search Bar */}
        <div className="p-4 border-b border-surface-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="relative w-full sm:w-80">
            <Icon
              name="search"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-lg"
            />
            <input
              type="text"
              placeholder="Ad, e-posta veya telefon ile ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-surface-container rounded-xl text-sm border border-transparent focus:border-primary focus:bg-surface-card outline-none transition-all"
            />
          </div>
          <p className="text-xs text-on-surface-variant">
            Gösterilen: <span className="font-semibold text-on-surface">{filteredAdmins.length}</span> yönetici
          </p>
        </div>

        {/* Content */}
        {loading ? (
          <div className="p-12 text-center text-on-surface-variant flex flex-col items-center gap-3">
            <Icon name="sync" className="text-3xl animate-spin text-primary" />
            <p>Sistem yöneticileri yükleniyor...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-rose-600 flex flex-col items-center gap-2">
            <Icon name="error" className="text-3xl" />
            <p>{error}</p>
            <button
              onClick={fetchAdmins}
              className="mt-2 px-4 py-1.5 bg-surface-container hover:bg-surface-container-high rounded-lg text-sm text-on-surface font-medium"
            >
              Tekrar Deneyin
            </button>
          </div>
        ) : filteredAdmins.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant flex flex-col items-center gap-2">
            <Icon name="search_off" className="text-4xl text-on-surface-variant/40" />
            <p className="font-medium text-on-surface">Yönetici Bulunamadı</p>
            <p className="text-xs text-on-surface-variant">
              Arama kriterlerinize uygun sistem yöneticisi mevcut değil.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-container text-xs text-on-surface-variant uppercase font-semibold border-b border-surface-border">
                <tr>
                  <th className="py-3.5 px-4">Yönetici Adı & E-posta</th>
                  <th className="py-3.5 px-4">Telefon</th>
                  <th className="py-3.5 px-4">Durum</th>
                  <th className="py-3.5 px-4">Güvenlik (2FA)</th>
                  <th className="py-3.5 px-4">Son Giriş</th>
                  <th className="py-3.5 px-4 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {filteredAdmins.map((admin) => {
                  const initial = admin.fullName?.charAt(0)?.toUpperCase() || "A";
                  const isLoadingAction = actionLoadingId === admin.id;

                  return (
                    <tr
                      key={admin.id}
                      className="hover:bg-surface-container-low transition-colors"
                    >
                      {/* Name & Email */}
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary-100 text-primary font-bold flex items-center justify-center text-sm shadow-sm">
                            {initial}
                          </div>
                          <div>
                            <p className="font-semibold text-on-surface">
                              {admin.fullName}
                            </p>
                            <p className="text-xs text-on-surface-variant">
                              {admin.email}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Phone */}
                      <td className="py-4 px-4 text-on-surface-variant font-mono text-xs">
                        {admin.phone || "—"}
                      </td>

                      {/* Status */}
                      <td className="py-4 px-4">
                        <div className="flex flex-col gap-1 items-start">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                              admin.active
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-rose-50 text-rose-700 border border-rose-200"
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                admin.active ? "bg-emerald-500" : "bg-rose-500"
                              }`}
                            />
                            {admin.active ? "Aktif" : "Pasif"}
                          </span>

                          {admin.mustChangePassword && (
                            <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded border border-amber-200 font-medium">
                              Şifre Değişimi Bekliyor
                            </span>
                          )}
                        </div>
                      </td>

                      {/* 2FA */}
                      <td className="py-4 px-4">
                        {admin.twoFactorEnabled ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full border border-purple-200">
                            <Icon name="verified_user" className="text-sm" /> 2FA Aktif
                          </span>
                        ) : (
                          <span className="text-xs text-on-surface-variant/60">
                            Pasif
                          </span>
                        )}
                      </td>

                      {/* Last Login */}
                      <td className="py-4 px-4 text-xs text-on-surface-variant">
                        {admin.lastLogin
                          ? new Date(admin.lastLogin).toLocaleString("tr-TR")
                          : "Henüz giriş yapmadı"}
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-4 text-right">
                        <button
                          onClick={() => handleToggleActive(admin)}
                          disabled={isLoadingAction}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                            admin.active
                              ? "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                          } disabled:opacity-50 cursor-pointer`}
                        >
                          {isLoadingAction ? (
                            <Icon name="sync" className="animate-spin text-sm" />
                          ) : admin.active ? (
                            "Pasife Al"
                          ) : (
                            "Aktifleştir"
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Admin Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-surface-card w-full max-w-md rounded-2xl border border-surface-border shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="p-6 border-b border-surface-border flex items-center justify-between bg-surface-container-low">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 text-primary rounded-xl">
                  <Icon name="person_add" className="text-xl" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-on-surface">
                    Yeni Sistem Yöneticisi Ekle
                  </h3>
                  <p className="text-xs text-on-surface-variant">
                    Sistem düzeyinde yetkili yeni admin hesabı oluşturun.
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-1 text-on-surface-variant hover:text-on-surface rounded-lg transition-colors cursor-pointer"
              >
                <Icon name="close" className="text-xl" />
              </button>
            </div>

            {/* Modal Body / Form */}
            <form onSubmit={handleCreateAdmin} className="p-6 space-y-4 overflow-y-auto flex-1">
              {formError && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs flex items-center gap-2">
                  <Icon name="error" className="text-base shrink-0" />
                  <span>{formError}</span>
                </div>
              )}

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-800 text-xs flex items-start gap-2">
                <Icon name="info" className="text-base text-blue-600 shrink-0 mt-0.5" />
                <p>
                  Yeni yöneticinin rolü otomatik olarak <strong>ADMIN</strong> olacaktır. Güvenlik gereği ilk girişinde şifresini değiştirmek zorundadır.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-on-surface mb-1.5">
                  E-Posta Adresi <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="ornek@beautysalon.com"
                  value={form.email}
                  onChange={handleFormChange}
                  className="w-full px-3.5 py-2.5 bg-surface-container rounded-xl text-sm border border-surface-border focus:border-primary focus:bg-surface-card outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-on-surface mb-1.5">
                  Ad Soyad <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  required
                  placeholder="Ahmet Yılmaz"
                  value={form.fullName}
                  onChange={handleFormChange}
                  className="w-full px-3.5 py-2.5 bg-surface-container rounded-xl text-sm border border-surface-border focus:border-primary focus:bg-surface-card outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-on-surface mb-1.5">
                  Telefon Numarası
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="+90 5xx xxx xx xx"
                  value={form.phone}
                  onChange={handleFormChange}
                  className="w-full px-3.5 py-2.5 bg-surface-container rounded-xl text-sm border border-surface-border focus:border-primary focus:bg-surface-card outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-on-surface mb-1.5">
                  Geçici Şifre <span className="text-rose-500">*</span>
                </label>
                <input
                  type="password"
                  name="password"
                  required
                  minLength={6}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={handleFormChange}
                  className="w-full px-3.5 py-2.5 bg-surface-container rounded-xl text-sm border border-surface-border focus:border-primary focus:bg-surface-card outline-none transition-all"
                />
              </div>

              {/* Modal Footer */}
              <div className="pt-4 border-t border-surface-border flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 bg-surface-container hover:bg-surface-container-high text-on-surface text-sm font-medium rounded-xl transition-colors cursor-pointer"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 bg-primary hover:bg-primary-600 text-white text-sm font-medium rounded-xl shadow-md transition-colors flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                >
                  {submitting ? (
                    <>
                      <Icon name="sync" className="animate-spin text-sm" />
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <span>Yönetici Oluştur</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
